import { Box } from '@mui/material';
import { Fragment, type JSX, useState } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { ControlsApi } from 'Api/Controls/ControlsApi';
import { Button } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldTextAreaAi } from 'Components/FormField/FormFieldTextAreaAi/FormFieldTextAreaAi';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Text } from 'Components/Text/Text';
import { ICON_SAVE, ICON_SUBMIT } from 'Config/Icons';
import { iso8601ToUsDateLong } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { useAiContentAdvisor } from 'Hooks/AiContentAdvisor';
import { AiContentAdvisorFeature } from 'Models/ArtificialIntelligence';
import { ComplianceRequirementForControlResponse } from 'Models/ComplianceRequirements';
import { AssessmentRole, AssessmentState, Effectiveness, EffectivenessSelectOptions, UpdateAssessmentRequest } from 'Models/OperationalControls';

import { CAState, CAStateAction, CAStateActionType } from '../ConductAssessment';
import styles from '../ConductAssessment.module.css';

export interface CurrentAssessmentProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    controlsApi: ControlsApi;
    dispatchCAStateChange: (action: CAStateAction) => void;
    handleRequestError: (error: Error) => void;
    loadCurrentAssessment: () => void;
    loadComplianceRequirements: () => void;
    caState: CAState;
    controlFramework: string;
    controlGroupId: string;
    controlId: string;
    hasReviewer: boolean;
}

/**
 * Enum for the different processing actions that can be taken on the assessment.
 * * APPROVE: The control reviewer approves the control assessment.
 * * SAVE: The control owner or control reviewer saves the control assessment without changing the workflow state.
 * * SUBMIT: The control owner submits the control assessment for review by the control reviewer.
 * * SUBMIT_AND_APPROVE: The control owner submits the control assessment and it is automatically approved because there is no control reviewer.
 */
enum ProcessingAction {
    APPROVE,
    SAVE,
    SUBMIT,
    SUBMIT_AND_APPROVE,
}

export const CurrentAssessment = (props: CurrentAssessmentProps): JSX.Element => {
    // State to track if an action requires confirmation before proceeding. This is used to show a modal to the user if they are not the control owner or control reviewer and are attempting to submit or approve the assessment.
    const [actionRequiresConfirmation, setActionRequiresConfirmation] = useState<ProcessingAction | null>(null);

    const formFieldChanged = (formFieldId: string, value: string | number | undefined): void => {
        const action: CAStateAction = {
            type: CAStateActionType.UpdateFormField,
            payload: {
                formFieldId: formFieldId,
                value: value,
            },
        };
        props.dispatchCAStateChange(action);
    };

    const formFieldChangedRegReq = (value: ChangeEventType, formFieldId: string): void => {
        const updatedComplianceRequirements = props.caState.currentAssessment.complianceRequirements ? [...props.caState.currentAssessment.complianceRequirements] : [];
        const tempComplianceRequirement = { ...updatedComplianceRequirements[parseInt(formFieldId)] };
        tempComplianceRequirement.status = value !== undefined ? String(value) : null;
        updatedComplianceRequirements[parseInt(formFieldId)] = tempComplianceRequirement;

        const action: CAStateAction = {
            type: CAStateActionType.UpdateFormField,
            payload: {
                formFieldId: 'complianceRequirements',
                value: updatedComplianceRequirements,
            },
        };
        props.dispatchCAStateChange(action);
    };

    /**
     * Handles the form submission for the current assessment.
     * This function determines the appropriate action to take based on the current workflow state and the user's roles, and either proceeds with the action or prompts for confirmation if they do not have the expected role (control owner or control reviewer, depending on the current workflow state).
     */
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();

        const inProgress = props.caState.currentAssessment.startedInProgress;
        const assessmentRoles = props.caState.controlDetails.assessmentRoles;

        let action: ProcessingAction | null = null;
        if (inProgress && props.hasReviewer && !assessmentRoles.has(AssessmentRole.OWNER)) {
            action = ProcessingAction.SUBMIT;
        } else if (inProgress && !props.hasReviewer && !assessmentRoles.has(AssessmentRole.OWNER)) {
            action = ProcessingAction.SUBMIT_AND_APPROVE;
        } else if (!inProgress && !assessmentRoles.has(AssessmentRole.REVIEWER)) {
            action = ProcessingAction.APPROVE;
        }

        if (action !== null) {
            setActionRequiresConfirmation(action);
            return;
        }

        proceedWithSubmit();
    };

    const proceedWithSubmit = (): void => {
        let action: CAStateAction = {
            type: CAStateActionType.UpdateUserInterfaceDisabled,
            payload: {
                formDisabled: true,
            },
        };
        props.dispatchCAStateChange(action);

        action = {
            type: CAStateActionType.UpdateUserInterfaceProcessing,
            payload: {
                isLoading: false,
                isSaving: false,
                isSubmitting: true,
            },
        };
        props.dispatchCAStateChange(action);

        if (props.caState.currentAssessment.startedInProgress && props.hasReviewer) {
            handleSubmitForReview();
        } else {
            handleApprove();
        }
    };

    /**
     * Handles the confirmation of an action that requires confirmation.
     * This is called when the user confirms that they want to proceed with changing the workflow state despite not being the control owner or control reviewer (depending on the current workflow state).
     */
    const handleConfirmAction = (): void => {
        setActionRequiresConfirmation(null);
        proceedWithSubmit();
    };

    const handleSubmitForReview = (): void => {
        updateAssessment(ProcessingAction.SUBMIT, AssessmentState.UNDER_REVIEW, props.caState.currentAssessment.ownerComment, undefined, props.caState.currentAssessment.effectiveness, props.caState.currentAssessment.complianceRequirements);
    };

    const handleApprove = (): void => {
        updateAssessment(ProcessingAction.APPROVE, AssessmentState.APPROVED, props.caState.currentAssessment.ownerComment, props.caState.currentAssessment.reviewerComment, props.caState.currentAssessment.effectiveness, props.caState.currentAssessment.complianceRequirements);
    };

    const handleSave = (): void => {
        const action: CAStateAction = {
            type: CAStateActionType.UpdateUserInterfaceProcessing,
            payload: {
                isLoading: false,
                isSaving: true,
                isSubmitting: false,
            },
        };
        props.dispatchCAStateChange(action);

        if (props.caState.currentAssessment.startedInProgress) {
            handleInProgressSave();
        } else {
            handleUnderReviewSave();
        }
    };

    const handleInProgressSave = (): void => {
        updateAssessment(ProcessingAction.SAVE, AssessmentState.IN_PROGRESS, props.caState.currentAssessment.ownerComment, undefined, props.caState.currentAssessment.effectiveness, props.caState.currentAssessment.complianceRequirements);
    };

    const handleUnderReviewSave = (): void => {
        updateAssessment(ProcessingAction.SAVE, AssessmentState.UNDER_REVIEW, props.caState.currentAssessment.ownerComment, props.caState.currentAssessment.reviewerComment, props.caState.currentAssessment.effectiveness, props.caState.currentAssessment.complianceRequirements);
    };

    const updateAssessment = async (processingAction: ProcessingAction, nextWorkflowState: AssessmentState, ownerComment?: string, reviewerComment?: string, effectiveness?: Effectiveness, complianceRequirements?: ComplianceRequirementForControlResponse[]): Promise<void> => {
        const action: CAStateAction = {
            type: CAStateActionType.UpdateUserInterfaceMessages,
            payload: {
                failureMessage: undefined,
                successMessage: undefined,
            },
        };
        props.dispatchCAStateChange(action);

        try {
            const updateAssessmentRequest: UpdateAssessmentRequest = {
                state: nextWorkflowState,
                owner_comment: ownerComment !== props.caState.currentAssessment.ownerCommentReference ? ownerComment : undefined,
                regulatory_requirements: JSON.stringify(complianceRequirements) !== JSON.stringify(props.caState.currentAssessment.complianceRequirementsReference) ? complianceRequirements : undefined,
                assessment_effectiveness: effectiveness !== props.caState.currentAssessment.effectivenessReference ? effectiveness : undefined,
                reviewer_comment: reviewerComment !== props.caState.currentAssessment.reviewerCommentReference ? reviewerComment : undefined,
            };
            await props.controlsApi.updateAssessment(props.controlFramework, props.controlGroupId, props.controlId, updateAssessmentRequest);

            /**
             * If the workflow has been approved, do not refresh the page and do not re-enable the form.
             * If the workflow has been saved or submitted, refresh the page and re-enable the form.
             * This allows a user who is both the Control Owner and Control Reviewer to continue the assessment, while disabling the form for a user who is only the Control Owner.
             */
            if (processingAction === ProcessingAction.APPROVE || processingAction === ProcessingAction.SUBMIT_AND_APPROVE) {
                const action: CAStateAction = {
                    type: CAStateActionType.UpdateUserInterfaceMessages,
                    payload: {
                        failureMessage: undefined,
                        successMessage: 'Assessment approved.',
                    },
                };
                props.dispatchCAStateChange(action);
            } else {
                props.loadCurrentAssessment();
                props.loadComplianceRequirements();

                let successMessage;
                if (processingAction === ProcessingAction.SAVE) {
                    successMessage = 'Assessment updated.';
                } else {
                    successMessage = 'Assessment submitted for review.';
                }

                let action: CAStateAction = {
                    type: CAStateActionType.UpdateUserInterfaceMessages,
                    payload: {
                        failureMessage: undefined,
                        successMessage: successMessage,
                    },
                };
                props.dispatchCAStateChange(action);

                action = {
                    type: CAStateActionType.UpdateUserInterfaceDisabled,
                    payload: {
                        formDisabled: false,
                    },
                };
                props.dispatchCAStateChange(action);
            }
        } catch (error) {
            props.handleRequestError(error);
            // Re-enable the form if there was an error so that the user doesn't have to refresh the page to correct it.
            const action: CAStateAction = {
                type: CAStateActionType.UpdateUserInterfaceDisabled,
                payload: {
                    formDisabled: false,
                },
            };
            props.dispatchCAStateChange(action);
        } finally {
            const action: CAStateAction = {
                type: CAStateActionType.UpdateUserInterfaceProcessing,
                payload: {
                    isLoading: false,
                    isSaving: false,
                    isSubmitting: false,
                },
            };
            props.dispatchCAStateChange(action);
        }
    };

    return (
        <div className={styles.fullWidth}>
            {actionRequiresConfirmation !== null && <RoleWarningConfirmationModal action={actionRequiresConfirmation} onConfirm={handleConfirmAction} onCancel={() => setActionRequiresConfirmation(null)} />}
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <Text variant="Header2">Current Control Assessment</Text>
                {props.caState.currentAssessment.dueDate && <DueDate timestamp={props.caState.currentAssessment.dueDate} />}
                <hr />
                <OwnerComment artificialIntelligenceApi={props.artificialIntelligenceApi} comment={props.caState.currentAssessment.ownerComment} disabled={props.caState.userInterface.formDisabled} handleChange={formFieldChanged} assessmentRoles={props.caState.controlDetails.assessmentRoles} handleRequestError={props.handleRequestError} />
                <ComplianceRequirements disabled={props.caState.userInterface.formDisabled} complianceRequirements={props.caState.currentAssessment.complianceRequirements} handleChange={formFieldChangedRegReq} assessmentRoles={props.caState.controlDetails.assessmentRoles} assessmentState={props.caState.currentAssessment.workflowState} />
                <EffectivenessDropdown workflowState={props.caState.currentAssessment.workflowState} disabled={props.caState.userInterface.formDisabled} effectiveness={props.caState.currentAssessment.effectiveness} handleChange={formFieldChanged} assessmentRoles={props.caState.controlDetails.assessmentRoles} />
                <ReviewerComment artificialIntelligenceApi={props.artificialIntelligenceApi} comment={props.caState.currentAssessment.reviewerComment} disabled={props.caState.userInterface.formDisabled} handleChange={formFieldChanged} startedInReview={props.caState.currentAssessment.startedInReview} assessmentRoles={props.caState.controlDetails.assessmentRoles} handleRequestError={props.handleRequestError} />
                <div className={styles.buttonContainer}>
                    <SaveButton disabled={props.caState.userInterface.formDisabled || props.caState.userInterface.isSaving || props.caState.userInterface.isSubmitting} workflowState={props.caState.currentAssessment.workflowState} handleSave={handleSave} isSavingAssessment={props.caState.userInterface.isSaving} assessmentRoles={props.caState.controlDetails.assessmentRoles} />
                    <SubmitForReviewButton disabled={props.caState.userInterface.formDisabled || props.caState.userInterface.isSaving || props.caState.userInterface.isSubmitting} isInProgress={props.caState.currentAssessment.startedInProgress} assessmentRoles={props.caState.controlDetails.assessmentRoles} isSubmittingAssessment={props.caState.userInterface.isSubmitting} hasReviewer={props.hasReviewer} />
                    <ApproveButton disabled={props.caState.userInterface.formDisabled || props.caState.userInterface.isSaving || props.caState.userInterface.isSubmitting} isInProgress={props.caState.currentAssessment.startedInProgress} isUnderReview={props.caState.currentAssessment.startedInReview} assessmentRoles={props.caState.controlDetails.assessmentRoles} isSubmittingAssessment={props.caState.userInterface.isSubmitting} hasReviewer={props.hasReviewer} />
                </div>
            </Box>
        </div>
    );
};

interface DueDateProps {
    timestamp: string;
}
const DueDate = (props: DueDateProps): JSX.Element => {
    return <Text variant="Text3">Due Date: {iso8601ToUsDateLong(props.timestamp)}</Text>;
};

interface OwnerCommentProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    comment?: string;
    disabled: boolean;
    handleChange: (formFieldId: string, value: string) => void;
    assessmentRoles: Set<AssessmentRole>;
    handleRequestError: (error: Error) => void;
}
const OwnerComment = (props: OwnerCommentProps): JSX.Element => {
    let disabled = false;
    if (props.disabled === true) {
        disabled = true;
    } else {
        disabled = !props.assessmentRoles.has(AssessmentRole.ADMIN) && !props.assessmentRoles.has(AssessmentRole.OWNER);
    }

    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        event.preventDefault();

        props.handleChange(event.currentTarget.id, event.currentTarget.value);
    };

    const ownerCommentAi = useAiContentAdvisor(props.artificialIntelligenceApi, props.comment ?? '', AiContentAdvisorFeature.GOVERNANCE_AND_COMPLIANCE_CONTROL_ASSESSMENT_OWNER_COMMENTS, (message: string) => props.handleRequestError(new Error(message)));

    return (
        <div className={styles.fieldContainer}>
            <FormFieldTextAreaAi disabled={disabled} value={props.comment} handleChange={handleChange} formFieldId="ownerComment" formFieldLabel="Owner Comment" aiContentAdvisorValues={ownerCommentAi} />
        </div>
    );
};

interface ComplianceRequirementsProps {
    disabled: boolean;
    complianceRequirements?: ComplianceRequirementForControlResponse[];
    handleChange: (value: ChangeEventType, formFieldId: string) => void;
    assessmentRoles: Set<AssessmentRole>;
    assessmentState: AssessmentState;
}
const ComplianceRequirements = (props: ComplianceRequirementsProps): JSX.Element | null => {
    const { getComplianceStatusOptions } = useCachedData();

    if (!props.complianceRequirements || props.complianceRequirements.length === 0) {
        return null;
    }
    const isInProgressAndOwnerOrAdmin = props.assessmentState === AssessmentState.IN_PROGRESS && (props.assessmentRoles.has(AssessmentRole.ADMIN) || props.assessmentRoles.has(AssessmentRole.OWNER));

    let disabled = false;
    if (props.disabled === true) {
        disabled = true;
    } else {
        disabled = props.assessmentState !== AssessmentState.UNDER_REVIEW && !isInProgressAndOwnerOrAdmin;
    }

    return (
        <Fragment>
            <Text>Select whether the following requirements are being met by this control.</Text>
            <div className={styles.alignVertical}>
                {props.complianceRequirements?.map((item: ComplianceRequirementForControlResponse, index) => (
                    <div className={styles.complianceRequirementsContainer} key={item.identifier}>
                        <div className={styles.regReqIdPadding}>
                            <Text variant="Text4">{`${item.regulation} ${item.identifier}`}</Text>
                        </div>
                        <div className={styles.regReqTextPadding}>
                            <Text variant="Text4">{item.text}</Text>
                            <div className={styles.fieldMaxWidthRequirement}>
                                <FormFieldSelect disabled={disabled} options={getComplianceStatusOptions(item.regulation)} handleChange={props.handleChange} formFieldId={String(index)} formFieldLabel="Requirement Status" selectedOption={item.status} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Fragment>
    );
};

interface EffectivenessDropdownProps {
    effectiveness?: Effectiveness;
    disabled: boolean;
    handleChange: (formFieldId: string, value: number | undefined) => void;
    workflowState: keyof typeof AssessmentState;
    assessmentRoles: Set<AssessmentRole>;
}
const EffectivenessDropdown = (props: EffectivenessDropdownProps): JSX.Element => {
    const isInProgressAndOwnerOrAdmin = props.workflowState === AssessmentState.IN_PROGRESS && (props.assessmentRoles.has(AssessmentRole.ADMIN) || props.assessmentRoles.has(AssessmentRole.OWNER));

    let disabled = false;
    if (props.disabled === true) {
        disabled = true;
    } else {
        disabled = props.workflowState !== AssessmentState.UNDER_REVIEW && !isInProgressAndOwnerOrAdmin;
    }

    const handleChange = (value: ChangeEventType, formFieldId: string): void => {
        props.handleChange(formFieldId, value !== undefined ? Number(value) : undefined);
    };

    return (
        <div className={`${styles.fieldContainer} ${styles.fieldMaxWidthEffectiveness}`}>
            <FormFieldSelect disabled={disabled} options={EffectivenessSelectOptions} handleChange={handleChange} formFieldId="effectiveness" selectedOption={props.effectiveness && Number(props.effectiveness)} formFieldLabel="Control Effectiveness" tooltip={'Sets the effectiveness of the control. The effectiveness should take evidence into consideration.'} required />
        </div>
    );
};

interface ReviewerCommentProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    comment?: string;
    disabled: boolean;
    handleChange: (formFieldId: string, value: string) => void;
    startedInReview: boolean;
    assessmentRoles: Set<AssessmentRole>;
    handleRequestError: (error: Error) => void;
}
const ReviewerComment = (props: ReviewerCommentProps): JSX.Element | null => {
    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        event.preventDefault();

        props.handleChange(event.currentTarget.id, event.currentTarget.value);
    };
    const reviewerCommentAi = useAiContentAdvisor(props.artificialIntelligenceApi, props.comment ?? '', AiContentAdvisorFeature.GOVERNANCE_AND_COMPLIANCE_CONTROL_ASSESSMENT_REVIEWER_COMMENTS, (message: string) => props.handleRequestError(new Error(message)));

    const shouldShow = props.startedInReview && (props.assessmentRoles.has(AssessmentRole.ADMIN) || props.assessmentRoles.has(AssessmentRole.REVIEWER));
    if (shouldShow) {
        return (
            <div className={styles.fieldContainer}>
                <FormFieldTextAreaAi disabled={props.disabled} value={props.comment} handleChange={handleChange} formFieldId="reviewerComment" formFieldLabel="Reviewer Comment" aiContentAdvisorValues={reviewerCommentAi} />
            </div>
        );
    }

    return null;
};

interface SaveButtonProps {
    assessmentRoles: Set<AssessmentRole>;
    disabled: boolean;
    handleSave: () => void;
    isSavingAssessment: boolean;
    workflowState: keyof typeof AssessmentState;
}
const SaveButton = (props: SaveButtonProps): JSX.Element | null => {
    const isInProgressAndOwnerOrAdmin = props.workflowState === AssessmentState.IN_PROGRESS && (props.assessmentRoles.has(AssessmentRole.ADMIN) || props.assessmentRoles.has(AssessmentRole.OWNER));
    const displayed = props.workflowState === AssessmentState.UNDER_REVIEW || isInProgressAndOwnerOrAdmin;

    if (displayed) {
        return (
            <Button variant="secondary" disabled={props.disabled} onClick={props.handleSave} isLoading={props.isSavingAssessment} loadingText="Saving..." fontAwesomeImage={ICON_SAVE}>
                Save
            </Button>
        );
    }

    return null;
};

interface SubmitForReviewButtonProps {
    assessmentRoles: Set<AssessmentRole>;
    disabled: boolean;
    isInProgress: boolean;
    isSubmittingAssessment: boolean;
    hasReviewer: boolean;
}
const SubmitForReviewButton = (props: SubmitForReviewButtonProps): JSX.Element | null => {
    const shouldShow = props.isInProgress && (props.assessmentRoles.has(AssessmentRole.ADMIN) || props.assessmentRoles.has(AssessmentRole.OWNER)) && props.hasReviewer;
    if (shouldShow) {
        return (
            <Button variant="submit" disabled={props.disabled} isLoading={props.isSubmittingAssessment} loadingText="Submitting...">
                Submit
            </Button>
        );
    } else {
        return null;
    }
};

interface ApproveButtonProps {
    assessmentRoles: Set<AssessmentRole>;
    disabled: boolean;
    isInProgress: boolean;
    isUnderReview: boolean;
    isSubmittingAssessment: boolean;
    hasReviewer: boolean;
}
const ApproveButton = (props: ApproveButtonProps): JSX.Element | null => {
    const shouldShow = (props.isUnderReview || (props.isInProgress && !props.hasReviewer)) && (props.assessmentRoles.has(AssessmentRole.ADMIN) || props.assessmentRoles.has(AssessmentRole.REVIEWER));
    if (shouldShow) {
        return (
            <Button variant="submit" disabled={props.disabled} isLoading={props.isSubmittingAssessment} loadingText="Approving...">
                APPROVE
            </Button>
        );
    } else {
        return null;
    }
};

interface RoleWarningConfirmationModalProps {
    action: ProcessingAction;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * A modal that warns the user that they are not the control owner or control reviewer (depending on the workflow state) and asks them to confirm that they want to proceed with the action.
 */
const RoleWarningConfirmationModal = (props: RoleWarningConfirmationModalProps): JSX.Element => {
    let headerText = 'Approve Assessment';
    let warningText = 'You are not the control reviewer. If you continue, the assessment will be approved.';
    let areYouSureText = 'Are you sure you want to approve this assessment?';
    let buttonText = 'Approve';
    let buttonLoadingText = 'Approving...';

    if (props.action === ProcessingAction.SUBMIT) {
        headerText = 'Submit Assessment';
        warningText = 'You are not the control owner. If you continue, the assessment will be submitted for review.';
        areYouSureText = 'Are you sure you want to submit this assessment?';
        buttonText = 'Submit';
        buttonLoadingText = 'Submitting...';
    } else if (props.action === ProcessingAction.SUBMIT_AND_APPROVE) {
        headerText = 'Approve Assessment';
        warningText = 'You are not the control owner. If you continue, the assessment will be approved.';
        areYouSureText = 'Are you sure you want to approve this assessment?';
        buttonText = 'Approve';
        buttonLoadingText = 'Approving...';
    }

    const performOperation = async (): Promise<string> => {
        props.onConfirm();
        return buttonLoadingText;
    };

    return (
        <ConfirmationModal operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE} headerText={headerText} areYouSureText={areYouSureText} performOperation={performOperation} hideModal={props.onCancel} buttonText={buttonText} buttonLoadingText={buttonLoadingText} buttonIcon={ICON_SUBMIT}>
            <Text>{warningText}</Text>
        </ConfirmationModal>
    );
};
